import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Send, Phone, Mail, MapPin, CheckCircle2, Clock, Calendar, ShieldCheck, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { enquiriesAPI } from '../api';
import SEO from '../components/SEO';
import { trackEvent } from '../lib/analytics';

const Contact = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    subject: '',
    message: '',
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contactName || !form.contactEmail || !form.message) {
      toast({
        title: 'Required Fields Missing',
        description: 'Name, email and message are required.',
        variant: 'destructive',
      });
      return;
    }
    setBusy(true);
    try {
      const { data } = await enquiriesAPI.create({
        serviceId: 'general',
        serviceName: form.subject || 'General Enquiry',
        contactName: form.contactName,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        message: form.message,
        extra: { subject: form.subject },
      });
      setSubmitted(data.enquiryId);
      trackEvent('generate_lead', {
        enquiry_id: data.enquiryId,
        source: 'contact_page',
        subject: form.subject || 'general',
      });
      toast({
        title: 'Message sent',
        description: `Reference ${data.enquiryId}. We'll be in touch within 1 business day.`,
      });
    } catch (err) {
      toast({
        title: 'Submission failed',
        description: err.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" />
              <h2 className="text-2xl font-bold text-gray-900">Message Received</h2>
              <p className="text-gray-600">
                Reference <strong>{submitted}</strong>. Our team will get back to{' '}
                <strong>{form.contactEmail}</strong> within 1 business day.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="contact-back-home"
              >
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="contact-page">
      <SEO
        title="Contact Consultium Group | Enquiries, Support & Appointments"
        description="Get in touch with Consultium Group. Call 012 824 3708, 078 285 0927 or 050 057 8315. WhatsApp 079 042 4911, 078 285 0927 or 050 057 8315. Email hello@consultiumgroup.co.za. Mon–Fri, 09:00–17:00 SAST. Appointments only."
        path="/contact"
      />
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Get in Touch</h1>
          <p className="text-gray-600">
            Ask us anything — about a service, your application, or how we can help your
            business grow.
          </p>
        </div>

        <div className="prose prose-sm max-w-4xl mx-auto text-gray-700 mb-10">
          <p>
            Consultium Group is a South African business services firm helping founders, growing
            SMEs and established companies stay compliant with CIPC, SARS and B-BBEE requirements.
            Whether you need to register a private company, appoint a Public Officer, file annual
            returns, or update directors and shareholders, our consultants can guide you through
            the process end-to-end. We also handle tax clearance status pins, VAT registrations,
            PAYE and UIF onboarding, and B-BBEE affidavits for exempted micro enterprises.
          </p>
          <p>
            The fastest way to get an accurate answer is a short written message telling us the
            business name (or intended name), the service you need, and the deadline you are
            working to. If your matter is urgent — a tender submission, a bank onboarding
            requirement, or a SARS notice with a due date — please mention it in the subject line
            and we will prioritise the enquiry.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Send us a message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        value={form.contactName}
                        onChange={(e) => set('contactName', e.target.value)}
                        data-testid="contact-name"
                        required
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={form.contactEmail}
                        onChange={(e) => set('contactEmail', e.target.value)}
                        data-testid="contact-email"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={form.contactPhone}
                        onChange={(e) => set('contactPhone', e.target.value)}
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <Label>Subject</Label>
                      <Input
                        value={form.subject}
                        onChange={(e) => set('subject', e.target.value)}
                        placeholder="e.g. Company Registration query"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Message *</Label>
                    <textarea
                      className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[140px]"
                      value={form.message}
                      onChange={(e) => set('message', e.target.value)}
                      placeholder="How can we help?"
                      data-testid="contact-message"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700 w-full"
                    data-testid="contact-submit"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {busy ? 'Sending…' : 'Send Message'}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    Your message will be sent to <strong>hello@consultiumgroup.co.za</strong>.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardContent className="py-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Phone className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                  <div className="w-full">
                    <p className="font-semibold text-gray-900">Call Centre</p>
                    <a
                      href="tel:0128243708"
                      className="text-emerald-700 hover:underline block"
                      data-testid="contact-call-centre-1"
                    >
                      012 824 3708
                    </a>
                    <a
                      href="tel:0782850927"
                      className="text-emerald-700 hover:underline block"
                      data-testid="contact-call-centre-2"
                    >
                      078 285 0927
                    </a>
                    <a
                      href="tel:0500578315"
                      className="text-emerald-700 hover:underline block"
                      data-testid="contact-call-centre-3"
                    >
                      050 057 8315
                    </a>
                    <p className="text-xs text-gray-500 mt-1">Operating hours: Mon–Fri, 09:00–17:00 SAST</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MessageCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                  <div className="w-full">
                    <p className="font-semibold text-gray-900">WhatsApp</p>
                    <a
                      href="https://wa.me/27790424911?text=Hi%20Consultium%20Group%2C%20I'd%20like%20to%20chat%20about%20your%20services."
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-emerald-700 hover:underline block"
                      data-testid="contact-whatsapp-1"
                    >
                      079 042 4911
                    </a>
                    <a
                      href="https://wa.me/27782850927?text=Hi%20Consultium%20Group%2C%20I'd%20like%20to%20chat%20about%20your%20services."
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-emerald-700 hover:underline block"
                      data-testid="contact-whatsapp-2"
                    >
                      078 285 0927
                    </a>
                    <a
                      href="https://wa.me/27500578315?text=Hi%20Consultium%20Group%2C%20I'd%20like%20to%20chat%20about%20your%20services."
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-emerald-700 hover:underline block"
                      data-testid="contact-whatsapp-3"
                    >
                      050 057 8315
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Email us</p>
                    <a
                      href="mailto:hello@consultiumgroup.co.za"
                      className="text-emerald-700 hover:underline"
                    >
                      hello@consultiumgroup.co.za
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-testid="contact-address">
                  <MapPin className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      Visit us <span className="text-xs font-normal text-gray-500">(Appointments Only)</span>
                    </p>
                    <p className="text-gray-700">
                      By appointment only.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Book an in-person consultation by emailing us or calling the numbers above.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="py-6 text-sm text-emerald-900">
                <p className="font-semibold mb-1">Why us?</p>
                <p>
                  Consultium handles all your CIPC, SARS and compliance work so you
                  can focus on running your business.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Business hours + service overview — richer content for SEO + user context */}
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="py-6">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="h-6 w-6 text-emerald-600" />
                <h3 className="text-lg font-semibold text-gray-900">Business Hours</h3>
              </div>
              <ul className="space-y-1 text-sm text-gray-700">
                <li className="flex justify-between">
                  <span>Monday – Friday</span>
                  <span className="font-medium">09:00 – 17:00</span>
                </li>
                <li className="flex justify-between text-gray-500">
                  <span>Saturday & Sunday</span>
                  <span>Closed</span>
                </li>
                <li className="flex justify-between text-gray-500">
                  <span>Public holidays</span>
                  <span>Closed</span>
                </li>
              </ul>
              <p className="text-xs text-gray-500 mt-3">
                All times are South African Standard Time (SAST, UTC+2). Enquiries received outside
                business hours are answered on the next working day.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-6">
              <div className="flex items-center gap-3 mb-3">
                <Calendar className="h-6 w-6 text-emerald-600" />
                <h3 className="text-lg font-semibold text-gray-900">Book a Consultation</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2">
                We meet clients strictly by appointment so that every session gets our full
                attention. Whether you need company registration, BEE strategy, or a compliance
                audit — reach out and we&apos;ll confirm a slot within one business day.
              </p>
              <p className="text-sm text-gray-700">
                Prefer to keep it remote? We handle 95% of applications end-to-end via email and
                WhatsApp, so an in-person visit is rarely required.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-6">
              <div className="flex items-center gap-3 mb-3">
                <ShieldCheck className="h-6 w-6 text-emerald-600" />
                <h3 className="text-lg font-semibold text-gray-900">What we do</h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>CIPC private company (Pty) Ltd registrations</li>
                <li>SARS tax clearance & registrations</li>
                <li>B-BBEE affidavits & certificates</li>
                <li>Annual returns & director changes</li>
                <li>ISO 9001 / 14001 / 45001 support</li>
                <li>Marketing, branding & digital services</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* SEO-friendly text block */}
        <div className="mt-10 max-w-3xl mx-auto text-sm text-gray-700 leading-relaxed space-y-4" data-testid="contact-info-content">
          <p>
            Consultium Group helps entrepreneurs, SMEs, and corporates stay compliant, certified,
            and growth-ready. With expertise in CIPC and SARS compliance, company registrations,
            annual returns, tax clearance certificates, B-BBEE and Equity Equivalent Investment
            Programme (EEIP) solutions, ISO certification support, and business growth strategies,
            we provide practical, reliable solutions under one roof. Reach out today and we will
            respond within one business day.
          </p>
          <h2 className="text-xl font-bold text-gray-900 mt-6">What to include when you contact us</h2>
          <p>
            A short, well-structured message helps us route your enquiry to the right consultant
            immediately. To register a new company please share the proposed company name choices
            (up to four), the full names, ID or passport numbers and email addresses of all
            directors and shareholders, the registered address, and a one-line description of the
            main business activity. For SARS-linked matters, include the company registration
            number, income tax reference and the SARS notice or letter you have received. For
            annual returns and beneficial ownership updates, confirm the company name, the year
            you are filing for, and whether any director or shareholder details have changed since
            the last submission.
          </p>
          <h2 className="text-xl font-bold text-gray-900 mt-6">How long will it take?</h2>
          <p>
            Once your documents are complete and payment is confirmed, CIPC private company
            registrations are typically finalised within 24 to 48 working hours, subject to CIPC
            name approval and system availability. Annual returns are usually filed the same
            business day. SARS tax clearance requests, Public Officer appointments and beneficial
            ownership submissions are processed as soon as we have received all required
            supporting documents from you. We do not promise fixed turnaround times where the
            outcome depends on the regulator, but we will update your file at every stage.
          </p>
          <h2 className="text-xl font-bold text-gray-900 mt-6">Prefer to browse first?</h2>
          <p>
            If you are still comparing options, our{' '}
            <a href="/faqs" className="text-emerald-700 hover:underline font-semibold">
              Frequently Asked Questions
            </a>{' '}
            page covers 26 practical questions across company registration, CIPC compliance and
            SARS tax matters. Our{' '}
            <a href="/blog" className="text-emerald-700 hover:underline font-semibold">
              Resources library
            </a>{' '}
            contains long-form guides on how to register a (Pty) Ltd, CIPC annual return deadlines
            and fees, and choosing between a B-BBEE affidavit or a full SANAS certificate.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
